import type { ReactNode } from 'react';
import { type FieldValues, useController } from 'react-hook-form';
import type {
  FormControlProps,
  TypedUseControllerProps,
} from '../FormControl/FormControl.types';
import {
  SelectField,
  type SelectFieldProps,
  type SelectMandatoryFieldProps,
} from '../SelectField/SelectField';

export type FormSelectProps<
  TFieldValues extends FieldValues = FieldValues,
  TValue extends string = string,
> = TypedUseControllerProps<TFieldValues, TValue | null> &
  FormControlProps<SelectFieldProps<TValue>>;

export type FormSelectMandatoryProps<
  TFieldValues extends FieldValues = FieldValues,
  TValue extends string = string,
> = TypedUseControllerProps<TFieldValues, TValue> &
  FormControlProps<SelectMandatoryFieldProps<TValue>>;

/**
 * React hook form Select control allowing null value
 */
const Optional = <
  TFieldValues extends FieldValues = FieldValues,
  TValue extends string = string,
>({
  disabled,
  ...props
}: FormSelectProps<TFieldValues, TValue>) => {
  const {
    field: { ref, ...field },
    fieldState: { error: fieldError },
  } = useController(props);

  const error = fieldError?.message?.toString();

  return (
    <SelectField.Optional
      {...props}
      {...field}
      disabled={disabled}
      error={error}
      inputRef={ref}
    />
  );
};

/**
 * React hook form Select control not allowing null value
 */
const Mandatory: <
  TFieldValues extends FieldValues = FieldValues,
  TValue extends string = string,
>(
  props: FormSelectMandatoryProps<TFieldValues, TValue>,
) => ReactNode = Optional as any;

export const FormSelect = { Mandatory, Optional };
